import React from 'react';
import AuthFloatingInput from '../../../components/auth/AuthFloatingInput';
import AuthFloatingSelect, {
  type AuthSelectOption,
} from '../../../components/auth/AuthFloatingSelect';
import { COUNTRIES } from '../../../config/countries';
import { COMPANY_SIZES } from '../../../types/settings';
import type { SignupFieldChangeHandler, SignupWizardData, SignupWizardErrors } from './types';

// Built once at module load: 249 countries do not change between renders.
const COUNTRY_OPTIONS: ReadonlyArray<AuthSelectOption> = COUNTRIES.map((country) => ({
  value: country.code,
  label: country.name,
}));

const COMPANY_SIZE_OPTIONS: ReadonlyArray<AuthSelectOption> = COMPANY_SIZES.map((size) => ({
  value: size,
  label: `${size} employees`,
}));

interface StepOrganizationProps {
  data: SignupWizardData;
  errors: SignupWizardErrors;
  disabled: boolean;
  onChange: SignupFieldChangeHandler;
}

/**
 * Step 2 of the sign-up wizard: the organization, i.e. the contracting
 * Customer the Terms are accepted on behalf of. Presentational — the host owns
 * state and validation.
 */
const StepOrganization: React.FC<StepOrganizationProps> = ({
  data,
  errors,
  disabled,
  onChange,
}) => (
  <div className="space-y-5">
    <div>
      <AuthFloatingInput
        label="Company legal name"
        id="companyLegalName"
        name="companyLegalName"
        type="text"
        autoComplete="organization"
        required
        value={data.companyLegalName}
        onChange={onChange}
        disabled={disabled}
        error={errors.companyLegalName}
        aria-invalid={errors.companyLegalName ? true : undefined}
        aria-describedby="companyLegalName-hint"
      />
      <p id="companyLegalName-hint" className="mt-1.5 text-xs text-text-secondary">
        The registered company or professional name that appears on your invoices.
      </p>
    </div>

    <div>
      <AuthFloatingSelect
        label="Country"
        id="country"
        name="country"
        autoComplete="country"
        required
        value={data.country}
        onChange={onChange}
        options={COUNTRY_OPTIONS}
        disabled={disabled}
        error={errors.country}
        aria-describedby="country-hint"
      />
      <p id="country-hint" className="mt-1.5 text-xs text-text-secondary">
        Where the organization is established. Determines the tax treatment of your subscription.
      </p>
    </div>

    <div>
      <AuthFloatingInput
        label="Workspace name (optional)"
        id="workspaceName"
        name="workspaceName"
        type="text"
        autoComplete="off"
        value={data.workspaceName}
        onChange={onChange}
        disabled={disabled}
        error={errors.workspaceName}
        aria-invalid={errors.workspaceName ? true : undefined}
        aria-describedby="workspaceName-hint"
      />
      <p id="workspaceName-hint" className="mt-1.5 text-xs text-text-secondary">
        What your team sees in the app. Leave it empty to use your company legal name.
      </p>
    </div>

    <AuthFloatingSelect
      label="Company size (optional)"
      id="companySize"
      name="companySize"
      value={data.companySize}
      onChange={onChange}
      options={COMPANY_SIZE_OPTIONS}
      disabled={disabled}
      error={errors.companySize}
    />
  </div>
);

export default StepOrganization;
